import React from 'react';
import { Box, Card, Stack, Typography, useTheme } from '@mui/material';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis } from 'recharts';
import { BlockSkeleton } from '../../../components/ui';
import { DashboardSummary } from '../../../types';
import { accent } from '../../../theme/tokens';
import { APP_LOCALE } from '../../../utils/locale';

interface WeekRevenueProps {
  summary: DashboardSummary | null;
  /** Shop-local "YYYY-MM-DD", so today's bar is the one picked out. */
  todayKey: string;
  loading: boolean;
}

const compact = (value: number) =>
  value >= 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${Math.round(value)}`;

const weekday = (iso: unknown) => {
  if (typeof iso !== 'string') return '';
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(APP_LOCALE, { weekday: 'short' });
};

/**
 * Money collected each day this week (Monday–Sunday, shop-local). One series:
 * every bar is the same measure, today's is picked out in the accent and the
 * rest stay neutral, so no legend is needed. Days still to come draw no bar —
 * nothing has been collected for them yet.
 */
const WeekRevenue: React.FC<WeekRevenueProps> = ({ summary, todayKey, loading }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const days = summary?.daily ?? [];
  const total = days.reduce((sum, day) => sum + day.revenue, 0);

  const highlight = isDark ? accent[400] : accent[500];
  const neutral = isDark ? theme.palette.grey[700] : theme.palette.grey[300];

  return (
    <Card sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" component="h2" sx={{ fontSize: '1rem' }}>
        Revenue this week
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Payments collected, Monday to Sunday
      </Typography>

      {loading && !summary ? (
        <Box sx={{ mt: 2 }}>
          <BlockSkeleton lines={5} />
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '1.875rem', fontWeight: 700, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums' }}>
              {total.toLocaleString(APP_LOCALE, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
            </Typography>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 190, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days} margin={{ top: 22, right: 0, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="date"
                  tickFormatter={weekday}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]} maxBarSize={34} isAnimationActive={false}>
                  {days.map((day) => (
                    <Cell key={day.date} fill={day.date === todayKey ? highlight : neutral} />
                  ))}
                  <LabelList
                    dataKey="revenue"
                    position="top"
                    formatter={(value: unknown) => (Number(value) > 0 ? compact(Number(value)) : '')}
                    style={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Card>
  );
};

export default WeekRevenue;
